import { Button } from "@/components/ui/baseUi/button"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useProgramOnOffPublish } from "@modules/programs/hooks/useProgramsOnOff"
import { Circles } from "@modules/ui/components/circles"
import { LoaderCircle } from "lucide-react"

type FillRateProps = {
  programId: string
  editionAllowed: boolean
} & React.ComponentProps<"div">

export function FillRate({ programId, editionAllowed, ...props }: FillRateProps) {
  const { data: program } = useProgram(programId)
  const { publishProgramOnOff, isPending: isPublishing } = useProgramOnOffPublish()

  const publishProgram = async () => {
    await publishProgramOnOff(programId)
      .then(() => {
        toast.success(word("program.onOff.publish.success"), { classNames: successToastClassName() })
      })
      .catch((e: unknown) => {
        if (typeof e === "object" && e !== null && "message" in e && typeof e.message === "string") {
          toast.error(e.message, { classNames: errorToastClassName() })
        }
      })
  }

  const calculFillRatePoints = () => {
    if (!program.duration) {
      return 0
    }
    const fillRate = (program.sessionCount / program.duration) * 100
    const fillRatePoints = Math.round(fillRate / 10)

    return Math.min(10, Math.max(0, fillRatePoints))
  }

  return (
    <div
      className="flex flex-col items-start gap-4 self-stretch rounded-4xl border border-neutral-200 p-5"
      style={props.style}
      {...props}
    >
      <div className="flex items-center">
        <WarningIcon color="var(--neutral-900)" width={24} height={24} className="mr-2" />
        <Text fontStyle="TM">{word("program.onOff.fillRate.title")}</Text>
      </div>

      <Text fontStyle="BM">{word("program.onOff.fillRate.description")}</Text>
      <Text fontStyle="BM">
        {`${program.sessionCount ?? 0}/${program.duration ?? 0}`}{" "}
        {word("global.day.label", { count: program.duration ?? 0 })}
      </Text>
      <Circles count={10} filled={calculFillRatePoints()} progressionstyle="line" />
      {editionAllowed && (
        <Button
          variant="primary"
          size="lg"
          disabled={program.sessionCount !== program.duration || isPublishing}
          onClick={() => void publishProgram()}
          isLoading={isPublishing}
        >
          {isPublishing && <LoaderCircle className="animate-spin" />}
          {word("program.button.publish")}
        </Button>
      )}
    </div>
  )
}
